import React from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { isAttachmentReadUrl } from "app/containers/Features/containers/Ticket/components/AttachmentPreview/helper/attachmentPreviewUrl";
import AttachmentPreviewSkeleton from "./PreviewSkeleton";


pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const MIN_PDF_BYTES = 1024;

type PdfPreviewProps = {
  fileUrl?: string;
  fileName?: string;
  zoomLevel?: number;
  loading?: boolean;
};

type PdfSource = Blob | string;

const PdfPreview: React.FC<PdfPreviewProps> = ({
  fileUrl,
  fileName,
  zoomLevel = 100,
  loading = false,
}) => {
  const [numPages, setNumPages] = React.useState(0);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [pdfSource, setPdfSource] = React.useState<PdfSource | null>(null);
  const [isFetching, setIsFetching] = React.useState(false);
  const [hasLoadError, setHasLoadError] = React.useState(false);
  const scale = zoomLevel / 100;

  React.useEffect(() => {
    setNumPages(0);
    setIsLoaded(false);
    setPdfSource(null);
    setHasLoadError(false);
    setIsFetching(false);

    if (!fileUrl || loading) return;

    if (!isAttachmentReadUrl(fileUrl)) {
      setHasLoadError(true);
      return;
    }

    let cancelled = false;
    setIsFetching(true);

    fetch(fileUrl, { method: "GET", mode: "cors", cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (blob.size < MIN_PDF_BYTES) {
          throw new Error(`PDF response too small (${blob.size} bytes)`);
        }
        return blob;
      })
      .then((blob) => {
        if (cancelled) return;
        setPdfSource(blob);
        setIsFetching(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPdfSource(fileUrl);
        setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl, loading]);

  if (!fileUrl || loading || isFetching) {
    return <AttachmentPreviewSkeleton zoomLevel={zoomLevel} />;
  }

  if (hasLoadError || !pdfSource) {
    return (
      <main className="h-full min-h-0 w-full bg-gray-100 overflow-auto">
        <div className="flex h-full w-full items-center justify-center p-6">
          <p className="text-center text-base font-normal leading-6 text-secondary">
            Unable to load this PDF. Try &quot;Open in new tab&quot; or download.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="h-full min-h-0 w-full bg-gray-100 relative">
      {!isLoaded ? (
        <div className="absolute inset-0 z-10">
          <AttachmentPreviewSkeleton zoomLevel={zoomLevel} />
        </div>
      ) : null}

      <div className="h-full w-full overflow-auto p-6 flex justify-center">
        <div className="w-fit max-w-full overflow-auto p-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <Document
            file={pdfSource}
            loading={null}
            onLoadSuccess={({ numPages: totalPages }) => {
              setNumPages(totalPages);
              setIsLoaded(true);
            }}
            onLoadError={() => {
              setHasLoadError(true);
              setIsLoaded(false);
            }}
          >
            {Array.from(new Array(numPages), (_item, index) => (
              <div
                key={`${fileName || "attachment-pdf"}-${index}`}
                className="mb-4 flex justify-center"
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  renderTextLayer
                  renderAnnotationLayer
                />
              </div>
            ))}
          </Document>
        </div>
      </div>
    </main>
  );
};

export default PdfPreview;
